import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getInvoicePdfHandler from "@tKgpco/Handlers/CustomInvoicePdfHandler";
import ShareEntityButton, { ShareEntityButtonStyles } from "@tKgpco/Overrides/Components/ShareEntityButton";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

const mapStateToProps = (state: CustomApplicationState) => ({
    showPricing:
        state.context.showPricing && state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
    discrepancyDetails: state.KGPState.DiscrepancyStore.discrepancyDetailsCollection?.discrepancyDetails,
});

const mapDispatchToProps = {
    getInvoicePdfHandler,
};

export interface DiscrepancyDetailHeaderStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
    loadingOverlay?: LoadingOverlayProps;
    spinner?: LoadingSpinnerProps;
    printButton?: ButtonPresentationProps;
    customPrintButton?: ButtonPresentationProps;
    printLabelWrapper?: InjectableCss;
    printLabelText?: InjectableCss;
    mobilePrintLabelSpinner?: LoadingSpinnerProps;
}

export const headerStyles: DiscrepancyDetailHeaderStyles = {
    shareEntityButtonStyles: {
        button: {
            css: css`
                margin-left: 10px;
            `,
            buttonType: "outline",
            variant: "secondary",
        },
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
            margin-bottom: 10px;
        `,
        width: [3, 3, 1, 6, 6],
    },
    titleGridItem: {
        width: [9, 9, 11, 6, 6],
        css: css`
            align-items: center;
        `,
    },
    title: {
        variant: "h2",
        as: "h2",
        css: css`
            @media print {
                font-size: 20px;
            }
        `,
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
    narrowOverflowMenu: {
        cssOverrides: {
            wrapper: css`
                button {
                    min-width: 50px;
                    width: auto;
                }
            `,
        },
    },
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
    customPrintButton: {
        shape: "pill",
        buttonType: "outline",
        css: css`
            margin-right: 5px;
            margin-bottom: 10px;
            min-width: 120px;
            border-width: 1px;
            span {
                text-transform: uppercase;
            }
        `,
    },
    printLabelWrapper: {
        css: css`
            display: flex;
        `,
    },
    printLabelText: {
        css: css`
            padding: 0 !important;
            background-color: inherit !important;
        `,
    },
    mobilePrintLabelSpinner: {
        size: 20,
    },
    printClickable: {
        css: css`
            span {
                text-transform: uppercase;
            }
        `,
    },
};

const styles = headerStyles;
const DiscrepancyDetailHeader: FC<
    WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>
> = ({ fields, getInvoicePdfHandler, showPricing, discrepancyDetails }) => {
    const [isFetching, setIsFetching] = React.useState(false);
    const toasterContext = useContext(ToasterContext);
    const { value: invoice } = useContext(InvoiceStateContext);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!discrepancyDetails) {
        return null;
    }

    const printLabel = (
        <StyledWrapper {...styles.printLabelWrapper}>
            <Typography as="span" {...styles.printLabelText}>
                {translate("Print")}
            </Typography>
            {isFetching ? <LoadingSpinner {...styles.mobilePrintLabelSpinner} /> : ""}
        </StyledWrapper>
    );

    const getPdf = () => {
        setIsFetching(true);
        // getInvoicePdfHandler({
        //     invoiceNumber: invoice.invoiceNumber,
        //     onSuccess: () => {
        //         setIsFetching(false);
        //         toasterContext.addToast({
        //             body: translate("Please click on the print button again if the pdf does not open."),
        //             messageType: "warning",
        //         });
        //     },
        //     onError: () => {
        //         setIsFetching(false);
        //     },
        // });
    };

    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography
                    {...styles.title}
                >{`${translate("Discrepancy #")}${discrepancyDetails.discrepancyNumber}`}</Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Hidden {...styles.menuHiddenContainer}>
                    <OverflowMenu hasChildPortal position="end" {...styles.narrowOverflowMenu}>
                        <ShareEntityButton
                            entityId={discrepancyDetails.discrepancyNumber}
                            variant="clickable"
                            entityName="Discrepancy"
                            extendedStyles={styles.shareEntityButtonStyles}
                            generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                            isExternalPage={true}
                        />
                        {/* {showPricing && (
                            <Clickable
                                {...styles.printClickable}
                                onClick={openPrintDialog}
                                data-test-selector="invoiceDetails_print"
                                disabled={isFetching}
                                target="_blank"
                            >
                                {printLabel}
                            </Clickable>
                        )} */}
                    </OverflowMenu>
                </Hidden>
                <Hidden {...styles.buttonsHiddenContainer}>
                    <ShareEntityButton
                        entityId={discrepancyDetails.discrepancyNumber}
                        entityName="Discrepancy"
                        extendedStyles={styles.shareEntityButtonStyles}
                        generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                        isExternalPage={true}
                    />
                </Hidden>
                {/* {showPricing && (
                    <Button
                        onClick={openPrintDialog}
                        icon={{ src: isFetching ? LoadingSpinner : "", position: "right" }}
                        {...styles.customPrintButton}
                    >
                        Print
                    </Button>
                )} */}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DiscrepancyDetailHeader),
    definition: {
        displayName: "Discrepancy Detail Header",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
